/**
 * DatasetListPage - Main page for viewing and managing datasets
 * @module components/starnexus/datasets/DatasetListPage
 *
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ContentLayout from '@amzn/awsui-components-console/content-layout';
import Header from '@amzn/awsui-components-console/header';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Alert from '@amzn/awsui-components-console/alert';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Input from '@amzn/awsui-components-console/input';
import FormField from '@amzn/awsui-components-console/form-field';

import Link from '@amzn/awsui-components-console/link';
import Toggle from '@amzn/awsui-components-console/toggle';

import { DatasetTable } from './DatasetTable';
import { DatasetFilters } from './DatasetFilters';
import { DatasetPickerModal } from './DatasetPickerModal';
import { useDatasets } from '../../../hooks/starnexus/useDatasets';
import { useAuthStore } from '../../../stores/starnexus/authStore';
import type { Dataset } from '../../../types/starnexus';
import { apiUrl, apiFetch, apiFetchDefaults } from '../../../utils/apiClient';

export const DatasetListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    datasets,
    totalCount,
    loading,
    error,
    currentPage,
    pageSize,
    totalPages,
    setCurrentPage,
    setPageSize,
    filterText,
    setFilterText,
    propertyFilters,
    setPropertyFilters,
    sortingColumn,
    setSortingColumn,
    selectedItems,
    setSelectedItems,
    bulkUpdateStatus,
    bulkDelete,
    refresh
  } = useDatasets({ initialPageSize: 10 });

  // Modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Dataset[] | null>(null);
  const [confirmInput, setConfirmInput] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Dataset picker modal state (triggered by nav link or button)
  const [pickerVisible, setPickerVisible] = useState(false);

  // My drafts filter
  const [showMyDrafts, setShowMyDrafts] = useState(false);
  const alias = useAuthStore((state) => state.alias);
  const currentAlias = alias ?? '';
  const draftCount = datasets.filter(
    (d) => d.status === 'draft' && d.owner === currentAlias
  ).length;
  const displayedDatasets = showMyDrafts
    ? datasets.filter((d) => d.status === 'draft' && d.owner === currentAlias)
    : datasets;

  useEffect(() => {
    if (searchParams.get('action') === 'upload-picker') {
      setPickerVisible(true);
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle single dataset action
  const handleAction = async (action: string, dataset: Dataset) => {
    switch (action) {
      case 'view':
        navigate(`/starnexus/uploader/datasets/${dataset.datasetId}`);
        break;
      case 'resume':
        navigate(`/starnexus/uploader/datasets/create?resume=${dataset.datasetId}`);
        break;
      case 'upload':
        // Navigate to detail page and signal it to auto-open the QuickUploadModal.
        navigate(`/starnexus/uploader/datasets/${dataset.datasetId}?action=upload`);
        break;
      case 'edit':
        navigate(
          `/starnexus/datasets/${dataset.datasetId}?tab=configuration`
        );
        break;
      case 'reactivate':
        setActionLoading(true);
        try {
          await bulkUpdateStatus([dataset.datasetId], 'active');
          setNotification({
            type: 'success',
            message: `Dataset "${dataset.datasetName}" reactivated`
          });
        } catch {
          setNotification({
            type: 'error',
            message: `Failed to reactivate dataset "${dataset.datasetName}"`
          });
        }
        setActionLoading(false);
        break;
      case 'archive':
        setActionLoading(true);
        try {
          await bulkUpdateStatus([dataset.datasetId], 'archived');
          setNotification({
            type: 'success',
            message: `Dataset "${dataset.datasetName}" archived`
          });
        } catch {
          setNotification({
            type: 'error',
            message: `Failed to archive dataset "${dataset.datasetName}"`
          });
        }
        setActionLoading(false);
        break;
      case 'delete':
        setDeleteTarget([dataset]);
        setDeleteModalVisible(true);
        break;
    }
  };

  // Handle bulk action
  const handleBulkAction = async (action: string) => {
    if (selectedItems.length === 0) return;

    const ids = selectedItems.map((d) => d.datasetId);

    switch (action) {
      case 'reactivate':
        setActionLoading(true);
        try {
          const result = await bulkUpdateStatus(ids, 'active');
          setNotification({
            type: 'success',
            message: `${result.success.length} dataset(s) reactivated`
          });
        } catch {
          setNotification({
            type: 'error',
            message: 'Failed to reactivate datasets'
          });
        }
        setActionLoading(false);
        break;
      case 'archive':
        setActionLoading(true);
        try {
          const result = await bulkUpdateStatus(ids, 'archived');
          setNotification({
            type: 'success',
            message: `${result.success.length} dataset(s) archived`
          });
        } catch {
          setNotification({
            type: 'error',
            message: 'Failed to archive datasets'
          });
        }
        setActionLoading(false);
        break;
      case 'delete':
        setDeleteTarget(selectedItems);
        setDeleteModalVisible(true);
        break;
      case 'export':
        // Mock export - just show notification
        setNotification({
          type: 'success',
          message: `Export started for ${selectedItems.length} dataset(s)`
        });
        break;
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setActionLoading(true);
    try {
      const ids = deleteTarget.map((d) => d.datasetId);
      const result = await bulkDelete(ids);
      setNotification({
        type: 'success',
        message: `${result.success.length} dataset(s) deleted successfully`
      });
      setDeleteModalVisible(false);
      setDeleteTarget(null);
      setConfirmInput('');
    } catch {
      setNotification({ type: 'error', message: 'Failed to delete datasets' });
    }
    setActionLoading(false);
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => navigate('/starnexus/uploader/datasets/create')}>
                Create dataset
              </Button>
              <Button variant="primary" onClick={() => setPickerVisible(true)}>
                Upload new snapshot
              </Button>
            </SpaceBetween>
          }
          description="View and manage your data processing datasets"
        >
          Datasets
        </Header>
      }
    >
      <SpaceBetween size="m">
        {/* Notification */}
        {notification && (
          <Alert
            type={notification.type}
            dismissible
            onDismiss={() => setNotification(null)}
          >
            {notification.message}
          </Alert>
        )}

        {/* Error state */}
        {error && (
          <Alert type="error" header="Error loading datasets">
            {error}
            <Button onClick={refresh}>Retry</Button>
          </Alert>
        )}

        {/* Drafts banner */}
        {draftCount > 0 && !showMyDrafts && (
          <Alert type="info" dismissible>
            You have {draftCount} unfinished draft dataset(s).{' '}
            <Link onFollow={() => setShowMyDrafts(true)}>View drafts</Link>
          </Alert>
        )}

        {/* My drafts toggle + cleanup button */}
        <SpaceBetween direction="horizontal" size="m" alignItems="center">
          <Toggle
            checked={showMyDrafts}
            onChange={({ detail }) => setShowMyDrafts(detail.checked)}
          >
            Show my drafts only ({draftCount})
          </Toggle>
          {showMyDrafts && (
            <Button
              iconName="remove"
              onClick={async () => {
                if (!window.confirm('Delete all draft datasets older than 7 days? This cannot be undone.')) return;
                try {
                  const res = await apiFetch(apiUrl('/api/drafts/cleanup-stale'), {
      ...apiFetchDefaults, method: 'POST' });
                  if (!res.ok) throw new Error(await res.text());
                  const data = await res.json();
                  alert(`Cleaned up ${data.cleaned} stale draft(s). ${data.errors?.length || 0} errors.`);
                  window.location.reload();
                } catch (e: any) {
                  alert(`Cleanup failed: ${e.message}`);
                }
              }}
            >
              Clean up drafts &gt; 7 days
            </Button>
          )}
        </SpaceBetween>

        {/* Filters */}
        <DatasetFilters
          filterText={filterText}
          onFilterTextChange={setFilterText}
          propertyFilters={propertyFilters}
          onPropertyFiltersChange={setPropertyFilters}
          loading={loading || actionLoading}
          totalCount={totalCount}
        />

        {/* Table */}
        <DatasetTable
          datasets={displayedDatasets}
          loading={loading || actionLoading}
          selectedItems={selectedItems}
          onSelectionChange={setSelectedItems}
          sortingColumn={sortingColumn}
          onSortingChange={setSortingColumn}
          currentPage={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          onAction={handleAction}
          onBulkAction={handleBulkAction}
        />
      </SpaceBetween>

      {/* Delete confirmation modal */}
      <Modal
        visible={deleteModalVisible}
        onDismiss={() => { setDeleteModalVisible(false); setConfirmInput(''); }}
        header={<Header variant="h2"><Box color="text-status-error" display="inline">Delete dataset permanently</Box></Header>}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => { setDeleteModalVisible(false); setConfirmInput(''); }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDeleteConfirm}
                loading={actionLoading}
                disabled={
                  deleteTarget?.length === 1
                    ? confirmInput !== deleteTarget[0].datasetName
                    : confirmInput !== `delete ${deleteTarget?.length} datasets`
                }
              >
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Alert type="warning">
            <SpaceBetween size="xs">
              <Box>This will permanently delete:</Box>
              <ul>
                <li>Dataset metadata (DynamoDB)</li>
                <li>Andes table (lifecycle transition to DELETED)</li>
                <li>Glue table + S3 transformed data</li>
              </ul>
              <Box fontWeight="bold">This action cannot be undone.</Box>
            </SpaceBetween>
          </Alert>
          {deleteTarget && deleteTarget.length > 1 && (
            <Box>
              <Box fontWeight="bold">Datasets to delete:</Box>
              <ul>
                {deleteTarget.map((d) => (
                  <li key={d.datasetId}>{d.datasetName}</li>
                ))}
              </ul>
            </Box>
          )}
          <FormField
            label={
              deleteTarget?.length === 1
                ? <>To confirm, type the dataset name: <Box display="inline" fontWeight="bold">{deleteTarget[0].datasetName}</Box></>
                : <>To confirm, type: <Box display="inline" fontWeight="bold">delete {deleteTarget?.length} datasets</Box></>
            }
          >
            <Input
              value={confirmInput}
              onChange={({ detail }) => setConfirmInput(detail.value)}
              placeholder={
                deleteTarget?.length === 1
                  ? deleteTarget[0].datasetName
                  : `delete ${deleteTarget?.length} datasets`
              }
            />
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Dataset picker for Upload snapshot */}
      <DatasetPickerModal
        visible={pickerVisible}
        onDismiss={() => setPickerVisible(false)}
        datasets={datasets}
      />
    </ContentLayout>
  );
};

export default DatasetListPage;
